import React, { useState, useEffect, useMemo } from 'react';
import { toFraction } from '../utils/quantityFormat';
import { formatConvertedAmount, ingredientUnitsMatch } from '../utils/weightUnits';
import './ShoppingListModal.css';

const ShoppingListModal = ({ 
  isOpen, 
  onClose, 
  aggregatedIngredients, 
  sourceRecipes,
  selectedDatesCount
}) => {
  const [checkedItems, setCheckedItems] = useState(new Set());
  const [sortBy, setSortBy] = useState('name'); // 'name', 'checked', 'category'

  // Reset checked items when modal opens with new ingredients
  useEffect(() => {
    if (isOpen) {
      // Load any previously checked items from localStorage
      const savedChecked = localStorage.getItem('shoppingListChecked');
      if (savedChecked) {
        try {
          const parsed = JSON.parse(savedChecked);
          setCheckedItems(new Set(parsed));
        } catch (e) {
          setCheckedItems(new Set());
        }
      } else {
        setCheckedItems(new Set());
      }
    }
  }, [isOpen]);

  // Save checked items to localStorage whenever they change
  useEffect(() => {
    if (checkedItems.size > 0) {
      localStorage.setItem('shoppingListChecked', JSON.stringify([...checkedItems]));
    }
  }, [checkedItems]);

  const handleToggleItem = (itemKey) => {
    const newChecked = new Set(checkedItems);
    if (newChecked.has(itemKey)) {
      newChecked.delete(itemKey);
    } else {
      newChecked.add(itemKey);
    }
    setCheckedItems(newChecked);
  };

  const handleClearAll = () => {
    setCheckedItems(new Set());
    localStorage.removeItem('shoppingListChecked');
  };

  const handleCheckAll = () => {
    // Only check valid ingredients (those that will be displayed)
    const validIngredients = aggregatedIngredients.filter(ing => {
      if (typeof ing === 'string') return ing.trim().length > 0;
      return ing.name && ing.name.trim().length > 0;
    });
    const allKeys = validIngredients.map((ing, idx) => getItemKey(ing, idx));
    setCheckedItems(new Set(allKeys));
  };

  const getItemKey = (ingredient, index) => {
    if (typeof ingredient === 'string') {
      return `str-${ingredient}-${index}`;
    }
    return `${ingredient.name.toLowerCase()}_${(ingredient.unit || '').toLowerCase()}_${index}`;
  };

  const getSourceRecipesForIngredient = (ingredientName, ingredientUnit, ingredientSource) => {
    // For food items, find matching food sources
    if (ingredientSource === 'food') {
      return sourceRecipes.filter(recipe => 
        recipe.isFood && recipe.title?.toLowerCase() === ingredientName?.toLowerCase()
      );
    }
    
    // For regular recipe ingredients
    return sourceRecipes.filter(recipe => 
      !recipe.isFood && recipe.ingredients?.some(ing => {
        if (typeof ing === 'string') {
          return ing.toLowerCase() === ingredientName?.toLowerCase();
        }
        return ing.name?.toLowerCase() === ingredientName?.toLowerCase() && 
          ingredientUnitsMatch(ing.unit, ingredientUnit);
      })
    );
  };

  // Format quantity for display
  const formatQuantity = (quantity, unit, isMass) => (
    isMass
      ? `${formatConvertedAmount(quantity, unit)} ${unit || ''}`.trim()
      : `${toFraction(quantity)} ${unit || ''}`.trim()
  );

  // Sort and filter ingredients based on selected sort option
  const sortedIngredients = useMemo(() => {
    // Filter out invalid ingredients (no name or empty name)
    const validIngredients = aggregatedIngredients.filter(ing => {
      if (typeof ing === 'string') return ing.trim().length > 0;
      return ing.name && ing.name.trim().length > 0;
    });
    
    const ingredientsCopy = validIngredients.map((ing, idx) => ({
      ...ing,
      originalIndex: idx,
      key: getItemKey(ing, idx)
    }));

    switch (sortBy) {
      case 'checked':
        return ingredientsCopy.sort((a, b) => {
          const aChecked = checkedItems.has(a.key);
          const bChecked = checkedItems.has(b.key);
          if (aChecked === bChecked) {
            return (a.name || '').localeCompare(b.name || '');
          }
          return aChecked ? 1 : -1;
        });
      case 'name':
      default:
        return ingredientsCopy.sort((a, b) => 
          (a.name || '').localeCompare(b.name || '')
        );
    }
  }, [aggregatedIngredients, sortBy, checkedItems]);

  // Calculate progress based on valid (filtered) ingredients count
  const validIngredientsCount = sortedIngredients.length;
  const progress = validIngredientsCount > 0 
    ? Math.round((checkedItems.size / validIngredientsCount) * 100)
    : 0;

  if (!isOpen) return null;

  return (
    <div className="shopping-list-overlay" onClick={onClose}>
      <div className="shopping-list-modal" onClick={(e) => e.stopPropagation()}>
        <div className="shopping-list-header">
          <div className="sl-header-top">
            <h2>
              <i className="fas fa-shopping-cart"></i>
              Shopping List
            </h2>
            <button className="sl-close-button" onClick={onClose}>
              <i className="fas fa-times"></i>
            </button>
          </div>
          
          <div className="sl-shopping-summary">
            <span className="sl-day-count">
              <i className="fas fa-calendar-day"></i>
              {selectedDatesCount} day{selectedDatesCount !== 1 ? 's' : ''} selected
            </span>
            <span className="sl-item-count">
              <i className="fas fa-list"></i>
              {validIngredientsCount} item{validIngredientsCount !== 1 ? 's' : ''}
            </span>
          </div>

          <div className="sl-progress-container">
            <div className="sl-progress-bar">
              <div 
                className="sl-progress-fill" 
                style={{ width: `${progress}%` }}
              />
            </div>
            <span className="sl-progress-text">
              {checkedItems.size} of {validIngredientsCount} items ({progress}%)
            </span>
          </div>
        </div>

        <div className="shopping-list-controls">
          <div className="sl-sort-controls">
            <label>Sort by:</label>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="name">Name (A-Z)</option>
              <option value="checked">Unchecked First</option>
            </select>
          </div>
          
          <div className="sl-bulk-actions">
            <button 
              className="sl-action-btn sl-clear-btn"
              onClick={handleClearAll}
              disabled={checkedItems.size === 0}
            >
              <i className="fas fa-undo"></i>
              Clear All
            </button>
            <button 
              className="sl-action-btn sl-check-all-btn"
              onClick={handleCheckAll}
              disabled={checkedItems.size === validIngredientsCount}
            >
              <i className="fas fa-check-double"></i>
              Check All
            </button>
          </div>
        </div>

        <div className="shopping-list-content">
          {sortedIngredients.length === 0 ? (
            <div className="sl-empty-list">
              <i className="fas fa-shopping-basket"></i>
              <p>No ingredients to shop for.</p>
              <p className="sl-hint">Select dates with meals to generate a shopping list.</p>
            </div>
          ) : (
            <ul className="sl-shopping-items">
              {sortedIngredients.map((ingredient) => {
                const isChecked = checkedItems.has(ingredient.key);
                const isFood = ingredient.source === 'food';
                const sources = typeof ingredient !== 'string' 
                  ? getSourceRecipesForIngredient(ingredient.name, ingredient.unit, ingredient.source)
                  : [];

                return (
                  <li 
                    key={ingredient.key}
                    className={`sl-shopping-item ${isChecked ? 'checked' : ''} ${isFood ? 'food-item' : ''}`}
                    onClick={() => handleToggleItem(ingredient.key)}
                  >
                    <div className="sl-item-checkbox">
                      <input 
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => {}}
                        className="sl-checkbox-input"
                      />
                      <span className="sl-checkbox-custom">
                        {isChecked && <i className="fas fa-check"></i>}
                      </span>
                    </div>
                    
                    <div className="sl-item-details">
                      <span className="sl-item-name">
                        {typeof ingredient === 'string' ? ingredient : ingredient.name}
                      </span>
                      {ingredient.brandOwner && (
                        <span className="sl-item-brand">{ingredient.brandOwner}</span>
                      )}
                      {typeof ingredient !== 'string' && ingredient.quantity > 0 && (
                        <span className="sl-item-quantity">
                          {formatQuantity(ingredient.quantity, ingredient.unit, ingredient.mass)}
                        </span>
                      )}
                    </div>

                    <div className="sl-item-sources">
                      {isFood ? (
                        <span className="sl-food-indicator" title="Individual Food">
                          <i className="fas fa-apple-whole"></i>
                        </span>
                      ) : sources.length > 0 ? (
                        <>
                          {sources.slice(0, 2).map((recipe, idx) => (
                            <img 
                              key={idx}
                              src={recipe.image}
                              alt={recipe.title}
                              title={recipe.title}
                              className="sl-source-thumb"
                              onError={(e) => {
                                e.target.src = 'https://storage.googleapis.com/freshmenulab/freshmenulab_logo.png';
                              }}
                            />
                          ))}
                          {sources.length > 2 && (
                            <span className="sl-more-sources">+{sources.length - 2}</span>
                          )}
                        </>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="shopping-list-footer">
          <div className="sl-footer-tip">
            <i className="fas fa-lightbulb"></i>
            <span>Tap items to check them off as you shop</span>
          </div>
          <button className="sl-done-button" onClick={onClose}>
            <i className="fas fa-check"></i>
            Done Shopping
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShoppingListModal;
